import React, { useState, useEffect } from 'react';
import { useI18n } from './i18n';
import { authFetch } from './auth';
import { ABTM_ELEMENTS, ABTM_GROUPS, ABTM_MAX_TOTAL } from './abtmSheet';
import { COFFEE_SCORED, COFFEE_CUPS, OIL_DEFECTS, OIL_POSITIVES } from './productScoring';

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8100/api'
  : '/api';

const isNum = (v) => v !== null && v !== undefined && v !== '' && !Number.isNaN(Number(v));
const TEA_RATINGS = ['dry_leaf', 'liquor_colour', 'aroma', 'taste', 'infused_leaf', 'overall'];
const VINEGAR_RESEARCH = [
  ['visual', ['visual_viscosity', 'visual_colour', 'visual_clearness', 'visual_gloss']],
  ['olfactory', ['olfactory_pungency', 'olfactory_persistence', 'olfactory_overall']],
  ['gustatory', ['gustatory_consistency', 'gustatory_sweetness', 'gustatory_acidity_intensity', 'gustatory_acidity_persistence',
    'gustatory_astringency', 'gustatory_saltiness', 'gustatory_bitterness']],
  ['final', ['overall']],
];
const CLASS_ORDER = ['outstanding', 'excellent', 'very_good', 'below_specialty'];

// The API groups its fields as documented in docs/schema-v2.md §5.0d; the lookups below accept a field at the top level
// of the answer or inside `stats` (and, for the panel-level oil fields, inside `panel`), so a small change of nesting does not break the panel.
const statsRoot = (summary) => summary.stats || summary;
const pick = (summary, key) => {
  const places = [summary, summary.stats, summary.stats && summary.stats.panel, summary.panel, summary.classification];   // real shape: stats.panel
  for (const place of places) if (place && place[key] !== undefined && place[key] !== null) return place[key];
  return undefined;
};
const statOf = (root, field) => {
  const value = root && (root[field] ?? (root.attributes && root.attributes[field]) ?? (root.elements && root.elements[field]));
  return value && typeof value === 'object' && isNum(value.mean) ? value : null;
};

// One attribute: bar = range min–max, dot = mean, line = median
function StatBar({ label, stat, min = 0, max = 10, children }) {
  const { t, formatNumber } = useI18n();
  if (!stat) return null;
  const pct = (v) => Math.max(0, Math.min(100, ((Number(v) - min) / (max - min)) * 100));
  const lo = pct(stat.min);
  const hi = pct(stat.max);
  const text = t('reports.summary.stat_line', {
    mean: formatNumber(stat.mean, 2), median: formatNumber(stat.median, 2),
    min: formatNumber(stat.min, 2), max: formatNumber(stat.max, 2), n: stat.n,
  });
  return (
    <div className="report-row">
      <span className="report-label">{label}</span>
      <span className="report-bar" role="img" aria-label={`${label}: ${text}`}>
        <span className="report-range" style={{ left: `${lo}%`, width: `${Math.max(hi - lo, 1)}%` }} />
        <span className="report-median" style={{ left: `${pct(stat.median)}%` }} />
        <span className="report-mean" style={{ left: `${pct(stat.mean)}%` }} />
      </span>
      <span className="report-values">{text}{children}</span>
    </div>
  );
}

function Bars({ root, fields, labelOf, min, max, withCv }) {
  const { t, formatNumber } = useI18n();
  return fields.map((field) => (
    <StatBar key={field} label={labelOf(field)} stat={statOf(root, field)} min={min} max={max}>
      {withCv && statOf(root, field) && (
        <span className="report-cv"> · {isNum(statOf(root, field).cvr)
          ? t('reports.oil.cv_value', { value: formatNumber(statOf(root, field).cvr, 1) })
          : t('reports.oil.cv_none')}</span>
      )}
    </StatBar>
  ));
}

function Group({ title, children }) {
  return <div className="report-group"><h4>{title}</h4>{children}</div>;
}

function CountChips({ counts, order, labelOf }) {
  const entries = (order || Object.keys(counts)).filter((key) => counts[key]);
  return entries.map((key) => <span key={key} className="chip report-chip">{labelOf(key)} · {counts[key]}</span>);
}

function Coffee({ summary }) {
  const { t, tc, formatNumber } = useI18n();
  const root = statsRoot(summary);
  const total = statOf(root, 'total_score');
  const cls = pick(summary, 'quality_class');
  const dist = pick(summary, 'class_distribution') || {};
  const roast = pick(summary, 'roast_level');
  return (
    <>
      <Group title={t('products.group.coffee.attributes')}>
        <Bars root={root} fields={COFFEE_SCORED} labelOf={(f) => t(`products.field.${f}`)} min={6} max={10} />
      </Group>
      <Group title={t('products.group.coffee.cups')}>
        <Bars root={root} fields={COFFEE_CUPS} labelOf={(f) => t(`products.field.${f}`)} min={0} max={5} />
      </Group>
      <Group title={t('reports.total_score')}>
        <StatBar label={t('reports.total_score')} stat={total} min={50} max={100} />
        <div className="report-chips">
          {cls && <span className="chip selected report-chip">{t('reports.quality_class')}: {tc('coffee.class', cls)}</span>}
          {total && <span className="report-values">{formatNumber(total.mean, 2)}/100</span>}
        </div>
        <div className="report-chips">
          <strong>{t('reports.class_distribution')}:</strong>
          <CountChips counts={dist} order={CLASS_ORDER} labelOf={(k) => tc('coffee.class', k)} />
        </div>
        {roast && <p className="report-note">{t('reports.roast_level')}: {t(`products.roast.${roast}`)}</p>}
      </Group>
    </>
  );
}

function Oil({ summary }) {
  const { t, tc, formatNumber } = useI18n();
  const root = statsRoot(summary);
  const bands = pick(summary, 'intensity_band') || {};
  const panelOk = pick(summary, 'panel_ok');
  const maxDefectMedian = pick(summary, 'max_defect_median');
  const n = Math.max(0, ...['fruity', 'bitter', 'pungent', ...OIL_DEFECTS].map((f) => (statOf(root, f) ? statOf(root, f).n : 0)));
  const sheets = summary.sheet_count ?? n;
  const cvr = (field) => { const s = statOf(root, field); return s && isNum(s.cvr) ? Number(s.cvr) : null; };
  const reasons = [];
  if (sheets < 8) reasons.push(t('reports.oil.reason_n', { n: sheets }));
  const cvTooHigh = ['fruity'].concat(maxDefectMedian > 0 ? OIL_DEFECTS.filter((f) => { const s = statOf(root, f); return s && Number(s.median) === Number(maxDefectMedian); }) : [])
    .filter((f) => cvr(f) !== null && cvr(f) > 20);
  if (cvTooHigh.length) reasons.push(t('reports.oil.reason_cv', { attrs: cvTooHigh.map((f) => t(`oil.p.${f}`)).join(', ') }));
  return (
    <>
      <div className="report-warning" role="note">
        <strong>{t('reports.oil.warning_title')}</strong> {t('reports.oil.warning')}
      </div>
      <p className={`report-panel ${panelOk ? 'ok' : 'not-ok'}`}>
        {panelOk
          ? t('reports.oil.panel_ok', { n: sheets })
          : t('reports.oil.panel_not_ok', { reason: reasons.join('; ') || t('reports.oil.reason_unknown') })}
      </p>
      <Group title={t('products.group.oil.defects')}>
        <Bars root={root} fields={OIL_DEFECTS.concat(['defect_other_intensity'])} labelOf={(f) => t(`oil.p.${f}`)} min={0} max={10} withCv />
      </Group>
      <Group title={t('products.group.oil.positives')}>
        <Bars root={root} fields={OIL_POSITIVES} labelOf={(f) => t(`oil.p.${f}`)} min={0} max={10} withCv />
      </Group>
      <Group title={t('reports.oil.panel_title')}>
        <div className="report-chips">
          {isNum(maxDefectMedian) && <span className="chip report-chip">{t('reports.oil.max_defect_median')}: {formatNumber(maxDefectMedian, 1)}</span>}
          {pick(summary, 'category_eu') && <span className="chip selected report-chip">{t('reports.oil.eu')}: {tc('oil.category', pick(summary, 'category_eu'))}</span>}
          {pick(summary, 'category_ioc') && <span className="chip report-chip">{t('oil.res.ioc')}: {tc('oil.category', pick(summary, 'category_ioc'))}</span>}
        </div>
        <div className="report-chips">
          {OIL_POSITIVES.filter((f) => bands[f]).map((f) => <span key={f} className="chip report-chip">{t(`oil.p.${f}`)}: {tc('oil.band', bands[f])}</span>)}
          {pick(summary, 'well_balanced') && <span className="chip selected report-chip">{t('oil.res.well_balanced')}</span>}
          {pick(summary, 'mild') && <span className="chip selected report-chip">{t('oil.res.mild')}</span>}
        </div>
        <p className="report-note">{t('reports.oil.cv_note')}</p>
      </Group>
    </>
  );
}

function Tea({ summary }) {
  const { t, tc } = useI18n();
  const root = statsRoot(summary);
  const category = pick(summary, 'gbt_category');
  return (
    <>
      <Group title={t('products.group.tea.ratings')}>
        <Bars root={root} fields={TEA_RATINGS} labelOf={(f) => t(`products.field.${f}`)} min={0} max={10} />
      </Group>
      <Group title={t('reports.total_score')}>
        <StatBar label={t('reports.total_unweighted')} stat={statOf(root, 'total_score')} min={0} max={50} />
        <StatBar label={t('reports.weighted_score')} stat={statOf(root, 'weighted_score')} min={0} max={100} />
        {category && <p className="report-note">{t('reports.gbt_category')}: {tc('tea.gbt', category)}</p>}
      </Group>
    </>
  );
}

// ABTM: how many tasters chose each level, with the official (Italian) descriptors
function AbtmElements({ data }) {
  const { t } = useI18n();
  return ABTM_GROUPS.map((group) => (
    <Group key={group} title={t(`vinegar.abtm.group.${group}`)}>
      {ABTM_ELEMENTS.filter((el) => el.group === group).map((el) => {
        const entry = (data && (data[el.field] ?? (data.elements && data.elements[el.field]))) || null;
        const raw = entry && (entry.level_distribution || entry.levels);
        const counts = [1, 2, 3, 4, 5].map((l) => Number(Array.isArray(raw) ? raw[l - 1] : (raw && (raw[l] ?? raw[String(l)]))) || 0);
        const top = Math.max(1, ...counts);
        return (
          <div key={el.field} className="report-abtm-row">
            <span className="report-label">
              {t(`vinegar.abtm.el.${el.field}`)}
              {entry && isNum(entry.points_mean) && <small className="report-hint"> · {t('reports.vinegar.points_mean')} {Math.round(Number(entry.points_mean) * 10) / 10}/{el.points[0]}</small>}
            </span>
            <span className="report-levels">
              {counts.map((count, i) => (
                <span key={i} className="report-level" style={{ opacity: count ? 0.35 + 0.65 * (count / top) : 0.18 }}
                  title={`${el.descriptors ? `${el.descriptors[i]} · ` : ''}${el.points[i]}`}>
                  <b>{count}</b><small>{el.points[i]}</small>
                </span>
              ))}
            </span>
          </div>
        );
      })}
    </Group>
  ));
}

function Vinegar({ summary }) {
  const { t } = useI18n();
  const root = statsRoot(summary);
  const protocols = root.protocols || summary.protocols || {};            // real shape: stats.protocols.<protocol>
  const abtm = protocols.abtm_dop || root.abtm_dop || summary.abtm_dop;
  const research = protocols.research_profile || root.research_profile || summary.research_profile;
  const abtmTotal = abtm && statOf(abtm, 'abtm_total');
  const hasAbtm = abtm && (abtmTotal || ABTM_ELEMENTS.some((el) => abtm[el.field] || (abtm.elements && abtm.elements[el.field])));
  const hasResearch = research && VINEGAR_RESEARCH.some(([, fields]) => fields.some((f) => statOf(research, f)));
  return (
    <>
      {hasAbtm && (
        <>
          <h4 className="report-protocol">{t('reports.vinegar.protocol_abtm')}</h4>
          <p className="report-note">{t('reports.vinegar.level_hint')}</p>
          <AbtmElements data={abtm} />
          <Group title={t('reports.abtm_total')}>
            <StatBar label={t('reports.abtm_total')} stat={abtmTotal} min={0} max={ABTM_MAX_TOTAL} />
          </Group>
        </>
      )}
      {hasResearch && (
        <>
          <h4 className="report-protocol">{t('reports.vinegar.protocol_research')}</h4>
          {VINEGAR_RESEARCH.map(([group, fields]) => (
            <Group key={group} title={t(`products.group.vinegar.${group}`)}>
              <Bars root={research} fields={fields} labelOf={(f) => t(`products.field.${f}`)} min={0} max={10} />
            </Group>
          ))}
        </>
      )}
      {!hasAbtm && !hasResearch && <p className="empty">{t('reports.summary.no_marks')}</p>}
    </>
  );
}

const BODY = { coffee: Coffee, oil: Oil, tea: Tea, vinegar: Vinegar };

// "Tasting summary" of a product: aggregation of the visible sheets of all tasters (GET /api/products/{id}/summary)
// Props: productId, category
function ProductSummary({ productId, category }) {
  const { t, formatDate } = useI18n();
  const [state, setState] = useState({ status: 'loading', data: null });

  useEffect(() => {
    let abort = false;
    setState({ status: 'loading', data: null });
    authFetch(`${API_URL}/products/${productId}/summary`)
      .then((r) => (r && r.ok ? r.json() : Promise.reject(r && r.status)))
      .then((data) => { if (!abort) setState({ status: 'ready', data }); })
      .catch(() => { if (!abort) setState({ status: 'error', data: null }); });
    return () => { abort = true; };
  }, [productId]);

  const { status, data } = state;
  const Body = BODY[(data && data.category) || category];
  return (
    <section className="report-summary" aria-live="polite">
      <h3>{t('reports.summary.title')}</h3>
      {status === 'loading' && <p className="empty">{t('reports.summary.loading')}</p>}
      {status === 'error' && <div className="message error">{t('reports.summary.error')}</div>}
      {status === 'ready' && (!data.sheet_count ? <p className="empty">{t('reports.summary.empty')}</p> : (
        <>
          <p className="report-counts">
            <span className="chip report-chip">{t('reports.summary.sheets', { n: data.sheet_count })}</span>
            <span className="chip report-chip">{t('reports.summary.tasters', { n: data.taster_count })}</span>
            {data.first_date && (
              <span className="chip report-chip">
                {data.last_date && data.last_date !== data.first_date
                  ? t('reports.summary.period', { from: formatDate(data.first_date), to: formatDate(data.last_date) })
                  : formatDate(data.first_date)}
              </span>
            )}
          </p>
          <p className="report-legend">{t('reports.summary.legend')}</p>
          {Body && <Body summary={data} />}
        </>
      ))}
    </section>
  );
}

export default ProductSummary;
